const BASE = '';

function getToken() {
  try { return localStorage.getItem('token'); }
  catch {
    try { return sessionStorage.getItem('token'); }
    catch { return null; }
  }
}

async function request(method, path, body, isForm = false) {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!isForm && body) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: isForm ? body : (body ? JSON.stringify(body) : undefined),
  });

  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
}

export const api = {
  // Auth
  register: (body) => request('POST', '/api/auth/register', body),
  login: (body) => request('POST', '/api/auth/login', body),
  me: () => request('GET', '/api/auth/me'),

  // Transactions
  uploadCSV: (formData) => request('POST', '/api/transactions/upload', formData, true),
  previewCSV: (formData) => request('POST', '/api/transactions/upload/preview', formData, true),
  confirmUpload: (body) => request('POST', '/api/transactions/upload/confirm', body),
  getTransactions: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null)).toString();
    return request('GET', `/api/transactions/${q ? '?' + q : ''}`);
  },
  updateTransaction: (id, body) => request('PATCH', `/api/transactions/${id}`, body),
  deleteTransaction: (id) => request('DELETE', `/api/transactions/${id}`),
  monthlySummary: (year) => request('GET', `/api/transactions/summary/monthly?year=${year}`),

  // Categories
  getCategories: () => request('GET', '/api/categories/'),
  createCategory: (body) => request('POST', '/api/categories/', body),
  updateCategory: (id, body) => request('PATCH', `/api/categories/${id}`, body),
  deleteCategory: (id) => request('DELETE', `/api/categories/${id}`),

  // Forecasts
  getForecast: (year, adjustments) => {
    const adj = adjustments ? `&scenario_adjustments=${encodeURIComponent(JSON.stringify(adjustments))}` : '';
    return request('GET', `/api/forecasts/forecast?year=${year}${adj}`);
  },
  getForecastWithAdjustments: (year, adjustments) => {
    const adj = `&scenario_adjustments=${encodeURIComponent(JSON.stringify(adjustments))}`;
    return request('GET', `/api/forecasts/forecast?year=${year}${adj}`);
  },
  getIncome: () => request('GET', '/api/forecasts/income'),
  addIncome: (body) => request('POST', '/api/forecasts/income', body),
  deleteIncome: (id) => request('DELETE', `/api/forecasts/income/${id}`),

  // Scenarios
  getScenarios: () => request('GET', '/api/scenarios/'),
  createScenario: (body) => request('POST', '/api/scenarios/', body),
  updateScenario: (id, body) => request('PATCH', `/api/scenarios/${id}`, body),
  deleteScenario: (id) => request('DELETE', `/api/scenarios/${id}`),

  // Manual / Bulk transactions
  addManualTransaction: (body) => request('POST', '/api/transactions/manual', body),
  addBulkTransactions: (rows) => request('POST', '/api/transactions/bulk', rows),

  // Investments
  getInvestments: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null)).toString();
    return request('GET', `/api/investments/${q ? '?' + q : ''}`);
  },
  addInvestment: (body) => request('POST', '/api/investments/', body),
  addBulkInvestments: (rows) => request('POST', '/api/investments/bulk', rows),
  updateInvestment: (id, body) => request('PATCH', `/api/investments/${id}`, body),
  deleteInvestment: (id) => request('DELETE', `/api/investments/${id}`),
  getInvestmentSummary: () => request('GET', '/api/investments/summary'),
  getSnapshots: () => request('GET', '/api/investments/snapshots'),
  addSnapshot: (body) => request('POST', '/api/investments/snapshots', body),
  deleteSnapshot: (id) => request('DELETE', `/api/investments/snapshots/${id}`),

  // Reconciliation
  getReconciliations: () => request('GET', '/api/reconciliation/'),
  createReconciliation: (body) => request('POST', '/api/reconciliation/', body),
  updateReconciliation: (id, body) => request('PATCH', `/api/reconciliation/${id}`, body),
  deleteReconciliation: (id) => request('DELETE', `/api/reconciliation/${id}`),
  quickCheck: (params) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null)).toString();
    return request('GET', `/api/reconciliation/quick-check?${q}`);
  },
};

// Append to existing api object — loans, networth, reports
Object.assign(api, {
  // Loans
  getLoans: () => request('GET', '/api/loans/'),
  getLoansSummary: () => request('GET', '/api/loans/summary'),
  addLoan: (body) => request('POST', '/api/loans/', body),
  updateLoan: (id, body) => request('PATCH', `/api/loans/${id}`, body),
  deleteLoan: (id) => request('DELETE', `/api/loans/${id}`),
  getLoanSchedule: (id) => request('GET', `/api/loans/${id}/schedule`),

  // Net worth
  getNetWorthSnapshots: () => request('GET', '/api/networth/snapshots'),
  addNetWorthSnapshot: (body) => request('POST', '/api/networth/snapshots', body),
  deleteNetWorthSnapshot: (id) => request('DELETE', `/api/networth/snapshots/${id}`),
  getLiveNetWorth: () => request('GET', '/api/networth/live'),

  // Reports
  getAnnualSummary: (year) => request('GET', `/api/reports/summary/${year}`),
  exportCSV: (year) => `${process.env.REACT_APP_API_URL || ''}/api/reports/export/${year}`,
});

// Upload history
Object.assign(api, {
  getUploadHistory: () => request('GET', '/api/transactions/upload/history'),
  deleteUploadFile: (filename) => request('DELETE', `/api/transactions/upload/history/${encodeURIComponent(filename)}`),
});

// Household
Object.assign(api, {
  getHousehold: () => request('GET', '/api/household/me'),
  createInvite: () => request('POST', '/api/household/invite'),
  joinHousehold: (token) => request('POST', '/api/household/join', { token }),
  removeMember: (userId) => request('DELETE', `/api/household/members/${userId}`),
});

// IOU
Object.assign(api, {
  getIouSummary: () => request('GET', '/api/iou/summary'),
  getIouEntries: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v != null)).toString();
    return request('GET', `/api/iou/entries${q ? '?' + q : ''}`);
  },
  createIouEntry: (body) => request('POST', '/api/iou/entries', body),
  settleIouEntry: (id) => request('PATCH', `/api/iou/entries/${id}/settle`, {}),
  deleteIouEntry: (id) => request('DELETE', `/api/iou/entries/${id}`),
  getIouCounterparts: () => request('GET', '/api/iou/counterparts'),
  createIouCounterpart: (body) => request('POST', '/api/iou/counterparts', body),
  deleteIouCounterpart: (id) => request('DELETE', `/api/iou/counterparts/${id}`),
});
